import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Snackbar,
  Typography,
} from '@mui/material';
import Alert from '@mui/material/Alert';
import {
  BOTTOM_BAR_OFFSET,
  ENV,
  PAGE_HEIGHT,
  SNACKBAR_DURATION,
} from '../constants';

import { post } from '../utils/fetch';
import PageLoader from './PageLoader';
import { authenticatedPost } from '../utils/authenticatedFetch';
import { useCurrentUser } from '../context/UserContext';

const styles = {
  main: {
    minHeight: PAGE_HEIGHT,
    padding: `20px 22px ${BOTTOM_BAR_OFFSET}`,
  },
  textInput: {
    margin: '20px 0',
  },
}

export function MagicLink() {
  const { code } = useParams();
  const { loadUser } = useCurrentUser();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [url, setUrl] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const { data: magicLinkResponse, error: magicLinkError } = useQuery({
    queryKey: ['getMagicLink', code],
    enabled: Boolean(code),
    queryFn: async () => post(`${ENV.API_URL}/urls/magic/${code}`),
  });

  useEffect(() => {
    if (magicLinkError) {
      enqueueSnackbar('Failed to find magic URL.', { variant: 'error' });
      navigate('/');
      return;
    }

    const resolveMagicLink = async () => {
      if (!magicLinkResponse) return;

      if (magicLinkResponse.status === 500) {
        enqueueSnackbar('Failed to find magic URL.', { variant: 'error' });
        navigate('/');
        return;
      }

      const result = await magicLinkResponse.json();
      if (result.data?.token) {
        setToken(result.data.token);
        setUrl(result.data.url);
      } else if (result.data?.url) {
        navigate(result.data.url);
      } else {
        enqueueSnackbar('Failed to find magic URL.', { variant: 'error' });
        navigate('/');
      }
    };

    resolveMagicLink();
  }, [magicLinkResponse, magicLinkError, enqueueSnackbar, navigate]);

  useEffect(() => {
    const loginWithMagicToken = async () => {
      const response = await authenticatedPost(`${ENV.API_URL}/login`, {
        token,
      });

      if (!response.ok) {
        enqueueSnackbar('Login failed.', { variant: 'error' });
        navigate('/');
        return;
      }

      await loadUser();
      navigate(url || '/');
    };

    if (token) {
      loginWithMagicToken();
    }
  }, [token, url, loadUser, enqueueSnackbar, navigate]);

  return <PageLoader />;
}

export function MagicCode() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loadUser } = useCurrentUser();
  const [code, setCode] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: '',
  });

  const { refetch: sendMagicCode } = useQuery({
    queryKey: ['sendMagicCode'],
    queryFn: () => post(`${ENV.API_URL}/urls/code/${code}`),
    enabled: false,
  });

  const onChange = (e) => {
    const { value } = e.target;
    const matchedValues = value.match(/\d+/g);
    const updatedValue = matchedValues ? matchedValues.join('') : '';

    setCode(updatedValue);
  };

  const onSubmitCode = async () => {
    if (!code) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid code!',
        type: 'error',
      });
      return;
    }

    const magicCodeResult = await sendMagicCode();
    if (magicCodeResult.error) {
      setSnackbar({
        open: true,
        message: magicCodeResult.error?.message || 'Failed to verify code.',
        type: 'error',
      });
      return;
    }

    const response = magicCodeResult.data;
    if (!response) {
      setSnackbar({
        open: true,
        message: 'Failed to verify code.',
        type: 'error',
      });
      return;
    }

    if (response.status === 500) {
      enqueueSnackbar('Failed to find magic URL.', { variant: 'error' });
      navigate('/');
      return;
    }

    const result = await response.json();
    if (result.data?.url && !result.data?.token) {
      navigate(result.data.url);
      return;
    }

    if (!result.data?.token) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid code!',
        type: 'error',
      });
      return;
    }

    const loginResponse = await authenticatedPost(`${ENV.API_URL}/login`, {
      token: result.data.token,
    });
    if (!loginResponse.ok) {
      setSnackbar({
        open: true,
        message: 'Login failed',
        type: 'error',
      });
      return;
    }

    await loadUser();
    navigate(result.data.url || '/');
  };

  return (
    <Box
      sx={styles.main}
      display="flex"
      alignItems="center"
      flexDirection="column"
      justifyContent="center"
    >
      <Typography variant="h1" color="primary">
        Code and URL sent!
      </Typography>
      <Typography level="h5" color="primary">
        Please check your text messages. Click the link or enter the code below
        to log in.
      </Typography>
      <FormControl variant="outlined" style={{ margin: '20px 0' }}>
        <InputLabel htmlFor="code">Enter Your Code</InputLabel>
        <OutlinedInput
          id="code"
          value={code}
          onChange={onChange}
          label="Enter Your Code"
          type="text"
          inputProps={{
            inputMode: 'numeric',
            autoComplete: 'one-time-code',
            pattern: '\\d{8}',
            'data-testid': 'magic-code-input',
          }}
        />
      </FormControl>
      <Button
        variant="contained"
        color="primary"
        disableElevation
        aria-label="Send Login Link"
        data-testid="magic-code-submit-button"
        onClick={onSubmitCode}
      >
        Log In
      </Button>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={SNACKBAR_DURATION}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.type}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
